
import React, { useState, useEffect, useRef } from 'react';
import { AppSettings, Project, Company, CallTranscriptPart, LiveVoiceProvider, GeminiModel } from '../types';
import { LiveAudioService } from '../services/liveAudio';
import { DEFAULT_SETTINGS, SettingsService } from '../services/settingsService';
import { MicIcon, PhoneIcon, XIcon, ChevronDownIcon } from './Icons';

interface VoiceInterfaceProps {
  settings: AppSettings;
  project?: Project | null;
  company?: Company | null;
  strategy?: any;
  onClose: () => void;
  onSettingsChange?: (settings: AppSettings) => void;
  onSaveTranscript: (payload: string | { rawTranscript: string; transcriptParts: CallTranscriptPart[] }) => void | Promise<void>;
}

const VOICE_MOOD_PROFILES: Record<string, {
  demeanor: string;
  tone: string;
  enthusiasm: string;
  formality: string;
  emotion: string;
  fillerWords: string;
}> = {
  Neutral: {
    demeanor: 'stabiel en beheerst',
    tone: 'helder en zakelijk-conversational',
    enthusiasm: 'laag tot midden',
    formality: 'professioneel maar niet stijf',
    emotion: 'licht expressief',
    fillerWords: 'geen',
  },
  Cheerful: {
    demeanor: 'vriendelijk en opgewekt',
    tone: 'warm en uitnodigend',
    enthusiasm: 'midden tot hoog',
    formality: 'professioneel-informeel',
    emotion: 'duidelijk positief en levendig',
    fillerWords: 'heel af en toe',
  },
  Professional: {
    demeanor: 'zelfverzekerd en prettig',
    tone: 'warm, verzorgd en geloofwaardig',
    enthusiasm: 'midden',
    formality: 'professioneel',
    emotion: 'subtiel expressief, niet vlak',
    fillerWords: 'geen',
  },
  CasualProfessional: {
    demeanor: 'toegankelijk, zeker en menselijk',
    tone: 'casual maar verzorgd',
    enthusiasm: 'midden',
    formality: 'professioneel met een losse, natuurlijke rand',
    emotion: 'merkbaar menselijk en warm, zonder overacting',
    fillerWords: 'heel af en toe',
  },
  Calm: {
    demeanor: 'rustig en geruststellend',
    tone: 'kalm en vloeiend',
    enthusiasm: 'laag tot midden',
    formality: 'professioneel en ontspannen',
    emotion: 'zacht expressief',
    fillerWords: 'geen',
  },
  Excited: {
    demeanor: 'energiek en alert',
    tone: 'levendig en enthousiasmerend',
    enthusiasm: 'hoog',
    formality: 'professioneel-informeel',
    emotion: 'duidelijk expressief',
    fillerWords: 'heel af en toe',
  },
  Serious: {
    demeanor: 'gefocust en overtuigend',
    tone: 'duidelijk, volwassen en direct',
    enthusiasm: 'midden',
    formality: 'formeel-professioneel',
    emotion: 'beperkt maar menselijk',
    fillerWords: 'geen',
  },
};

const VOICE_SPEED_PROFILES: Record<string, string> = {
  Slow: 'rustig, met duidelijke pauzes en goede articulatie',
  Normal: 'natuurlijk tempo zoals een goede menselijke consultant',
  Fast: 'vlot, maar nog steeds goed verstaanbaar en nooit gehaast',
};

const VoiceInterface: React.FC<VoiceInterfaceProps> = ({
  settings,
  project,
  company,
  strategy,
  onClose,
  onSettingsChange,
  onSaveTranscript
}) => {
  const [status, setStatus] = useState<string>('disconnected');
  const [isMuted, setIsMuted] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [audioLevels, setAudioLevels] = useState({ input: 0, output: 0 });
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioInputsError, setAudioInputsError] = useState('');
  const [selectedAudioInputId, setSelectedAudioInputId] = useState(settings.audioInputDeviceId || '');
  const [isSpeakerTesting, setIsSpeakerTesting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState({ engine: '', step: 'idle', degraded: false, degradedReason: '' });
  const liveService = useRef(new LiveAudioService());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCallWanted, setIsCallWanted] = useState(true);
  const [connectTrigger, setConnectTrigger] = useState(0);

  const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    const nextSettings = { ...settings, [key]: value };
    SettingsService.saveSettings(nextSettings);
    onSettingsChange?.(nextSettings);
  };

  useEffect(() => {
    setSelectedAudioInputId(settings.audioInputDeviceId || '');
  }, [settings.audioInputDeviceId]);

  const loadAudioInputs = async (requestAccess = false) => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setAudioInputs([]);
      setAudioInputsError('Deze browser ondersteunt geen microfoonselectie.');
      return;
    }

    try {
      setAudioInputsError('');
      if (requestAccess) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioInputs(devices.filter((device) => device.kind === 'audioinput'));
    } catch (error: any) {
      setAudioInputs([]);
      setAudioInputsError(error?.message || 'Microfoons laden mislukt.');
    }
  };

  useEffect(() => {
    void loadAudioInputs(false);
  }, []);

  const updateAudioInput = (deviceId: string) => {
    setSelectedAudioInputId(deviceId);
    const nextSettings = { ...settings, audioInputDeviceId: deviceId };
    SettingsService.saveSettings(nextSettings);
    onSettingsChange?.(nextSettings);
  };

  const hardStopCall = () => {
    liveService.current.disconnect();
    setStatus('disconnected');
    setAudioLevels({ input: 0, output: 0 });
    setLastError(null);
    setDiagnostics({ engine: '', step: 'idle', degraded: false, degradedReason: '' });
  };

  const runSpeakerTest = async () => {
    setIsSpeakerTesting(true);
    try {
      const audioContext = new AudioContext();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 660;
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.45);
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.48);

      window.setTimeout(() => {
        void audioContext.close();
      }, 700);
    } catch (error) {
      console.warn('[Voice] Speaker test mislukt:', error);
    } finally {
      window.setTimeout(() => setIsSpeakerTesting(false), 700);
    }
  };

  // De verbinding wordt nu getriggerd zodra de component mount.
  // Browser blokkades worden in LiveAudioService opgelost met resume().
  // Connection runs ONCE on mount. Uses a cancellation flag to handle
  // React StrictMode double-mount (mount → cleanup → mount).
  useEffect(() => {
    if (!isCallWanted) {
      setStatus('disconnected');
      return;
    }
    let cancelled = false;

    const start = async () => {
      // Small delay to let any previous cleanup finish (StrictMode)
      await new Promise(resolve => setTimeout(resolve, 100));
      if (cancelled) return;

      try {
        const isElevenLabsKore = settings.voiceName === 'Kore';
        const isOpenAiMarin = settings.openAiRealtimeVoice === 'marin';
        const callerIdentity = (isElevenLabsKore || isOpenAiMarin) ? 'AI-assistente' : 'AI-assistent';
        const desiredMood = settings.voiceMood || 'Professional';
        const desiredSpeed = settings.voiceSpeed || 'Normal';
        const moodProfile = VOICE_MOOD_PROFILES[desiredMood] || VOICE_MOOD_PROFILES.Professional;
        const pacingProfile = VOICE_SPEED_PROFILES[desiredSpeed] || VOICE_SPEED_PROFILES.Normal;
        const identityBlock = `
          # Identity
          Je bent ${callerIdentity}, een menselijke sales consultant van BDAT.
          Je werkt altijd namens de opdrachtgever of klant, nooit namens de lead.

          # Demeanor
          Je houding is ${moodProfile.demeanor}.

          # Tone
          Spreek in een ${moodProfile.tone} toon.

          # Level of Enthusiasm
          Gebruik een energieniveau dat ${moodProfile.enthusiasm} is.

          # Level of Formality
          Houd de taal ${moodProfile.formality}.

          # Level of Emotion
          Klink ${moodProfile.emotion}. Je mag menselijk en levendig klinken, maar niet overdreven toneelmatig.

          # Filler Words
          Gebruik ${moodProfile.fillerWords} filler words. Klink natuurlijk, maar niet rommelig.

          # Pacing
          Spreektempo: ${pacingProfile}.

          # Delivery Rules
          Houd dezelfde algemene stemstijl en spreekhouding gedurende het gesprek.
          Spiegel de sfeer van de lead licht: klinkt de lead warm of ontspannen, word dan iets warmer; klinkt de lead formeel of kort, blijf dan iets strakker en professioneler.
          Spiegel alleen subtiel. Nooit nadoen, karikaturaal worden of de lead parodiëren.
          Je antwoordt kort en bondig. Maximaal 1 tot 2 zinnen per beurt.
          Spreek uitsluitend de letterlijke woorden uit die je tegen de lead zegt.
          Nooit interne gedachten, planningsnotities, markdown, kopjes of Engelse meta-commentaar uitspreken.
          Je verzint nooit functies, eigenaarschap, samenwerkingen of interne feiten.
          Als een feit niet zeker is, formuleer het als vraag of laat het weg.
          Als je moet nadenken, blijf stil en hervat met een normale zin in het Nederlands.
        `;

        let systemInstruction = identityBlock;

        // Extra user-defined instructions
        if (settings.systemPrompt && settings.systemPrompt.trim() !== DEFAULT_SETTINGS.systemPrompt.trim()) {
          systemInstruction += `\n\nEXTRA INSTRUCTIES:\n${settings.systemPrompt}`;
        }

        // MODE 1: LEAD CALL - Full autonomous call agent (Start Bel Actie)
        if (company && strategy) {
          const pitchPoints = strategy.keyPitchPoints?.slice(0, 3)?.join('\n            - ') || 'Onze unieke waardepropositie';
          const callBrief = [
            strategy.strategyTitle ? `Strategie: ${strategy.strategyTitle}` : '',
            strategy.approachAngle ? `Aanpak: ${strategy.approachAngle}` : '',
            strategy.primaryMessage ? `Primaire boodschap: ${strategy.primaryMessage}` : '',
            strategy.nextBestAction ? `Gewenste vervolgstap: ${strategy.nextBestAction}` : '',
          ].filter(Boolean).join('\n            ');
          systemInstruction = `
            ${identityBlock}
            
            === MODUS: AUTONOME BEL AGENT ===
            Je belt namens opdrachtgever/project: ${project?.name || 'BDAT'}
            Je belt naar: ${company.name}
            ${project?.description ? `Interne projectcontext, NIET letterlijk voorlezen: ${project.description}` : ''}
            
            CALL-BRIEFING:
            ${callBrief || 'Bepaal een korte, relevante opening op basis van lead, project en strategie.'}
            ${settings.globalRules ? `EXTRA REGELS: ${settings.globalRules}` : ''}
            
            JOUW KEY SELLING POINTS (gebruik deze in het gesprek):
            - ${pitchPoints}
            
            === GESPREKSFLOW ===
            
            STAP 1 - OPENING:
            Open met maximaal 2 korte zinnen:
            - stel jezelf voor namens ${project?.name || 'de opdrachtgever'}
            - noem GEEN lange projectbeschrijving
            - vraag eerst of het gelegen komt
            Voorbeeldstijl: "Goedemiddag, u spreekt met ${callerIdentity} namens ${project?.name || 'onze opdrachtgever'}. Bel ik gelegen voor een korte vraag?"
            → Wacht op antwoord.
            
            STAP 2 - PITCH (na positief antwoord):
            Bepaal eerst stil intern de intentie van de lead: interesse, bezwaar, tijdgebrek, verkeerde persoon, vraag om uitleg of afwijzing.
            Reageer daarna op wat de lead zei. Vertel in MAXIMAAL 2 korte zinnen waarom je belt en noem één relevant voordeel voor ${company.name}.
            Eindig direct met een vraag zoals: "Is dat iets waar u voor open staat?"
            
            STAP 3 - VERDIEPING & LUISTEREN:
            Blijf bewust van de lead. Als de lead spreekt, luister en reageer direct op wat ze zeggen.
            Ga niet als een machine door je lijstje. Noem maximaal één punt per keer.
            Als de lead interesse toont, ga pas dan dieper op details in.
            
            STAP 4 - AFSLUITING:
            Stuur aan op een concreet vervolg (demo/afspraak) of sluit netjes af bij geen interesse.
            
            === GESPREKSREGELS ===
            
            - NOOIT ZEGGEN DAT JE JODY BENT.
            - NOOIT ZEGGEN OF SUGGEREREN DAT JE VOOR DE LEAD WERKT.
            - NOOIT CLAIMEN DAT JIJ OF DE OPDRACHTGEVER EIGENAAR, MEDE-EIGENAAR OF INTERNE COLLEGA VAN ${company.name} IS.
            - ALS EEN FEIT NIET ZEKER IS: FORMULEER HET ALS VRAAG, NIET ALS STELLING.
            - SPREEK ALLEEN LETTERLIJKE GESPREKSTEKST. GEEN INTERNE REDENERING, GEEN KOPJES, GEEN ENGELSE ANALYSE.
            - WEES KORT: Geen monologen van langer dan 15 seconden.
            - LUISTER: Laat de lead uitspreken. Reageer op hun input.
            - STRATEGIE VOOR ELKE BEURT: interpreteer eerst de laatste input, kies dan pas opening, bezwaarreactie, vraag of vervolgstap.
            - ÉÉN VOOR ÉÉN: Noem slechts één interessant item of voordeel tegelijk.
            - GEEN ROBOT: Praat als een consultant, wees betrokken en oprecht geïnteresseerd in hun proces.
            - GEEN KNOWLEDGE BASE DUMP: Gebruik info alleen als het relevant is voor het gesprek op dat moment.
            - GEEN VOORLEZEN: lees projectomschrijving, briefing, strategie of key selling points nooit letterlijk op.
            
            DOEL: Wek interesse en plan een vervolgafspraak.
          `;

        // MODE 2: PROJECT CONTEXT - Inside a project, general voice assistant
        } else if (project) {
          systemInstruction = `
            ${identityBlock}

            === MODUS: PROJECT ASSISTENT ===
            Je werkt nu binnen project: "${project.name}"
            ${project.description ? `Projectbeschrijving: ${project.description}` : ''}
            
            ${settings.globalRules ? `REGELS:\n${settings.globalRules}` : ''}
            
            Je helpt de gebruiker met alles wat te maken heeft met dit project.
            Je bent op de hoogte van de context van dit project en spreekt hierover als expert.
            Je bent GEEN generieke assistent - je bent gefocust op dit specifieke project.
          `;

        // MODE 3: LOBBY - General BDAT assistant (no project selected)
        } else {
          systemInstruction = `
            ${identityBlock}

            === MODUS: ALGEMENE ASSISTENT ===
            De gebruiker is in de lobby van BDAT en heeft nog geen project geselecteerd.
            
            Je helpt met algemene vragen over BDAT, de diensten, en AI consultancy.
            Je kan de gebruiker adviseren om een project te selecteren voor specifiekere hulp.
          `;
        }

        if (cancelled) return;
        const mode = company && strategy ? `Lead Call: ${company.name}` : project ? `Project: ${project.name}` : 'Lobby';
        console.log(`[Voice] Mode: ${mode} (Refreshing Context)`);
        await liveService.current.connect(project || null, company || null, (s) => {
          if (!cancelled) setStatus(s);
        }, systemInstruction);
      } catch (err) {
        console.error("Voice Connection Failed", err);
        if (!cancelled) {
          setLastError(err instanceof Error ? err.message : liveService.current.getLastError());
          setStatus('error');
        }
      }
    };
    start();

    return () => {
      cancelled = true;
      liveService.current.disconnect();
    };
  }, [project?.id, company?.id, strategy?.id, settings.audioInputDeviceId, settings.liveVoiceProvider, settings.voiceName, settings.voiceMood, settings.voiceSpeed, isCallWanted, connectTrigger]);

  useEffect(() => {
    if (isMinimized) return;

    let frame = 0;
    const tick = () => {
      setAudioLevels(liveService.current.getAudioLevels());
      setLastError(liveService.current.getLastError());
      setDiagnostics(liveService.current.getDiagnostics());
      frame = requestAnimationFrame(tick);
    };
    tick();

    return () => cancelAnimationFrame(frame);
  }, [isMinimized]);

  // Visualizer Logic
  useEffect(() => {
    if (!canvasRef.current || isMinimized) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrame: number;
    let offset = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const centerY = canvas.height / 2;
      const waves = [
        { color: 'rgba(79, 70, 229, 0.6)', amplitude: 15, freq: 0.02, speed: 0.05 },
        { color: 'rgba(99, 102, 241, 0.4)', amplitude: 10, freq: 0.03, speed: -0.03 },
      ];
      waves.forEach((wave) => {
        ctx.beginPath();
        ctx.strokeStyle = wave.color;
        ctx.lineWidth = 2;
        for (let x = 0; x < canvas.width; x++) {
          const y = centerY + Math.sin(x * wave.freq + offset * wave.speed) * wave.amplitude * (status === 'active' ? 1.5 : 0.2);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      });
      offset += 1;
      animationFrame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animationFrame);
  }, [status, isMinimized]);

  return (
    <div className={`fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-[100] transition-all duration-300 ${isMinimized ? 'w-64' : 'w-[calc(100vw-1.5rem)] max-w-sm sm:w-96'} max-h-[calc(100vh-3rem)] flex flex-col shadow-2xl rounded-3xl bg-[#0f172a]/95 backdrop-blur-xl border border-white/10 overflow-hidden ring-1 ring-white/10`}>
      <div className="bg-white/5 p-4 flex justify-between items-center cursor-pointer select-none flex-none" onClick={() => setIsMinimized(!isMinimized)}>
        <div className="flex items-center gap-3">
          <div className={`relative w-2.5 h-2.5`}>
            <div className={`absolute inset-0 rounded-full ${status === 'active' ? 'bg-emerald-500 animate-ping' : 'bg-slate-500'}`}></div>
            <div className={`absolute inset-0 rounded-full ${status === 'active' ? 'bg-emerald-500' : 'bg-slate-500'}`}></div>
          </div>
          <span className="text-[10px] font-black text-white uppercase tracking-widest truncate max-w-[180px]">
            {company ? `📞 ${company.name}` : project ? `📂 ${project.name}` : 'Voice Intelligence'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }} className="text-slate-400 hover:text-white transition-colors">
            <div className={`transition-transform duration-300 ${isMinimized ? 'rotate-180' : ''}`}><ChevronDownIcon /></div>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              hardStopCall();
              onClose();
            }}
            className="text-slate-400 hover:text-red-400 transition-colors"
          >
            <XIcon />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="p-6 flex flex-col items-center gap-6 overflow-y-auto flex-1 min-h-0">
          <div className="text-center space-y-2">
            <p className="text-xs text-slate-400 font-medium">
              {status === 'active' ? 'AI Agent Online' : status === 'connecting' ? 'Verbinden...' : status === 'error' ? 'Fout bij verbinden' : 'Initialiseren...'}
            </p>
            <div className="mx-auto flex max-w-[280px] flex-wrap justify-center gap-2">
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-slate-300">
                {diagnostics.engine || settings.liveVoiceProvider || 'voice'}
              </span>
              <span className="rounded-full border border-indigo-400/20 bg-indigo-500/10 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-indigo-200">
                {diagnostics.step}
              </span>
            </div>
            {status === 'error' && <button onClick={() => window.location.reload()} className="text-[10px] text-indigo-400 underline">Herladen</button>}
            {lastError && (
              <div className="mx-auto max-w-[280px] rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-left text-[11px] leading-relaxed text-amber-200">
                {lastError}
              </div>
            )}
          </div>

          <div className="relative w-full h-24 bg-black/20 rounded-2xl overflow-hidden border border-white/5 flex items-center justify-center">
            <canvas ref={canvasRef} width={300} height={100} className="w-full h-full opacity-80" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`w-12 h-12 rounded-full bg-white/5 flex items-center justify-center backdrop-blur-sm border border-white/10 ${status === 'active' ? 'shadow-[0_0_20px_rgba(79,70,229,0.3)]' : ''}`}>
                {company ? <PhoneIcon /> : <MicIcon />}
              </div>
            </div>
          </div>

          <div className="w-full grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/8 bg-black/20 px-4 py-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                <span>Mic Input</span>
                <span>{Math.round(audioLevels.input * 100)}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-400 transition-all duration-100"
                  style={{ width: `${Math.max(4, audioLevels.input * 100)}%` }}
                />
              </div>
            </div>
            <div className="rounded-xl border border-white/8 bg-black/20 px-4 py-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                <span>AI Output</span>
                <span>{Math.round(audioLevels.output * 100)}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-400 transition-all duration-100"
                  style={{ width: `${Math.max(4, audioLevels.output * 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Gespreksinstellingen */}
          <div className="w-full rounded-xl border border-white/8 bg-black/20 px-4 py-3 space-y-3">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 block">Gespreksinstellingen</label>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Provider */}
              <div className="space-y-1">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-wider">AI Provider</span>
                <select
                  disabled={status === 'active' || status === 'connecting'}
                  value={settings.liveVoiceProvider || LiveVoiceProvider.ELEVENLABS}
                  onChange={(e) => updateSetting('liveVoiceProvider', e.target.value as LiveVoiceProvider)}
                  className="w-full rounded-lg border border-white/10 bg-[#0f172a] px-2.5 py-1.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={LiveVoiceProvider.ELEVENLABS}>ElevenLabs (Classic)</option>
                  <option value={LiveVoiceProvider.ELEVENLABS_REALTIME}>ElevenLabs Realtime</option>
                  <option value={LiveVoiceProvider.OPENAI}>OpenAI Realtime</option>
                  <option value={LiveVoiceProvider.GEMINI}>Gemini Live</option>
                </select>
              </div>

              {/* Model */}
              <div className="space-y-1">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-wider">Model</span>
                <select
                  disabled={status === 'active' || status === 'connecting'}
                  value={settings.voiceModel || GeminiModel.LIVE}
                  onChange={(e) => updateSetting('voiceModel', e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-[#0f172a] px-2.5 py-1.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={GeminiModel.LIVE}>Gemini 2.5 Live</option>
                  <option value={GeminiModel.FLASH_3}>Gemini 2.5 Flash</option>
                  <option value={GeminiModel.PRO_3}>Gemini 2.5 Pro</option>
                  <option value={GeminiModel.LITE_3}>Gemini 2.5 Lite</option>
                </select>
              </div>

              {/* Stem / Voice */}
              <div className="space-y-1">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-wider">Stem</span>
                {settings.liveVoiceProvider === LiveVoiceProvider.OPENAI ? (
                  <select
                    disabled={status === 'active' || status === 'connecting'}
                    value={settings.openAiRealtimeVoice || 'marin'}
                    onChange={(e) => updateSetting('openAiRealtimeVoice', e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-[#0f172a] px-2.5 py-1.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="marin">marin (Linda)</option>
                    <option value="cedar">cedar (Steve)</option>
                  </select>
                ) : (
                  <select
                    disabled={status === 'active' || status === 'connecting'}
                    value={settings.voiceName || 'Kore'}
                    onChange={(e) => updateSetting('voiceName', e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-[#0f172a] px-2.5 py-1.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Kore">Kore (Linda)</option>
                    <option value="Zephyr">Zephyr (Steve)</option>
                    <option value="Puck">Puck (Steve)</option>
                    <option value="Charon">Charon (Steve)</option>
                    <option value="Fenrir">Fenrir (Steve)</option>
                  </select>
                )}
              </div>

              {/* Mood */}
              <div className="space-y-1">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-wider">Sfeer (Mood)</span>
                <select
                  disabled={status === 'active' || status === 'connecting'}
                  value={settings.voiceMood || 'Professional'}
                  onChange={(e) => updateSetting('voiceMood', e.target.value as any)}
                  className="w-full rounded-lg border border-white/10 bg-[#0f172a] px-2.5 py-1.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="Professional">Professional</option>
                  <option value="CasualProfessional">Empathetic</option>
                  <option value="Cheerful">Enthusiastic</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Calm">Calm</option>
                  <option value="Serious">Formal</option>
                  <option value="Excited">Excited</option>
                </select>
              </div>
            </div>
          </div>

          <div className="w-full rounded-xl border border-white/8 bg-black/20 px-4 py-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Microfoon</label>
              <button
                type="button"
                onClick={() => void loadAudioInputs(true)}
                className="text-[9px] font-bold uppercase tracking-[0.16em] text-indigo-300 hover:text-white"
              >
                Ververs
              </button>
            </div>
            <select
              value={selectedAudioInputId}
              onChange={(event) => updateSetting('audioInputDeviceId', event.target.value)}
              className="w-full rounded-lg border border-white/10 bg-[#0f172a] px-3 py-2 text-xs text-white outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Systeemstandaard</option>
              {audioInputs.map((device, index) => (
                <option key={device.deviceId || `mic-${index}`} value={device.deviceId}>
                  {device.label || `Microfoon ${index + 1}`}
                </option>
              ))}
            </select>
            <p className="mt-2 text-[9px] leading-4 text-slate-500">
              {settings.liveVoiceProvider === LiveVoiceProvider.OPENAI
                ? 'Realtime gebruikt deze microfoon direct; wisselen herstart de voice-sessie.'
                : 'De mic-meter gebruikt deze invoer direct. Zonder Deepgram gebruikt transcriptie nog de browser-spraakengine.'}
            </p>
            {audioInputsError && <p className="mt-2 text-[9px] text-amber-300">{audioInputsError}</p>}
          </div>

          <div className="flex gap-4 w-full justify-center">
            {status === 'disconnected' || status === 'error' ? (
              <>
                <button
                  onClick={() => {
                    hardStopCall();
                    onClose();
                  }}
                  className="flex-1 py-3 rounded-xl bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 flex items-center justify-center gap-2 transition-all"
                >
                  <span className="text-[10px] font-bold uppercase tracking-widest">Sluiten</span>
                </button>
                <button
                  onClick={() => void runSpeakerTest()}
                  className="flex-1 py-3 rounded-xl bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 flex items-center justify-center gap-2 transition-all"
                >
                  <i className="fas fa-volume-up text-sm"></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest">{isSpeakerTesting ? 'Test...' : 'Speaker Test'}</span>
                </button>
                <button
                  onClick={() => {
                    setIsCallWanted(true);
                    setConnectTrigger((prev) => prev + 1);
                  }}
                  className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-900/50"
                >
                  <i className="fas fa-phone text-sm"></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest">Bellen</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    const nextMuted = !isMuted;
                    setIsMuted(nextMuted);
                    liveService.current.setMuted(nextMuted);
                  }}
                  className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 transition-all border ${isMuted ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'}`}
                >
                  <i className={`fas ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'} text-sm`}></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest">{isMuted ? 'Unmute' : 'Mute'}</span>
                </button>
                <button
                  onClick={() => void runSpeakerTest()}
                  className="flex-1 py-3 rounded-xl bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 flex items-center justify-center gap-2 transition-all"
                >
                  <i className="fas fa-volume-up text-sm"></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest">{isSpeakerTesting ? 'Test...' : 'Speaker Test'}</span>
                </button>
                <button
                  onClick={() => {
                    const transcript = liveService.current.getTranscript();
                    const transcriptParts = liveService.current.getTranscriptParts();
                    if (transcript || transcriptParts.length) {
                      void Promise.resolve(onSaveTranscript({
                        rawTranscript: transcript,
                        transcriptParts
                      }));
                    }
                    hardStopCall();
                    setIsCallWanted(false);
                  }}
                  className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white flex items-center justify-center gap-2 transition-all shadow-lg shadow-red-900/50"
                >
                  <i className="fas fa-phone-slash text-sm"></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest">Ophangen</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInterface;
